import { Button, Row, TextArea, TextInput } from '@app/components';
import { InfoBulle } from '@app/components/form/InfoBulle';
import { TEXTE_AIDE_HORAIRES } from '@app/components/form/aide-horaires';
import { ScheduleForm } from '@app/components/form/ScheduleForm';
import { ScheduleAvailabilityTypeEnum } from '@app/common';
import { Toggle } from '@app/components/form/Toggle';
import React, { forwardRef, useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { defaultProductVariationsFormValues } from './product-form.helpers';
import { ProductVariationForm } from './ProductVariationForm';
import { OptionSelect } from '@app/components/form/selects/features/OptionSelect';
import { TagSelect } from '@app/components/form/selects/features/TagSelect';
import { StyledForms } from '@app/components/styled/form/Form';
import { InputFile, InputFileRef } from '@app/components/form/InputFile';
import { AllergenSelect } from '@app/components/form/selects/features/AllergenSelect';
import { IngredientSelect } from '@app/components/form/selects/features/IngredientSelect';
import { KitchenAreaSelect } from '@app/components/form/selects/features/KitchenAreaSelect';
import { FormSection } from '@app/components/styled/form/FormSection';
import { HelperText } from '@app/components/styled/form/Label';

export const ProductForm = forwardRef<any>((_, ref: React.ForwardedRef<InputFileRef>) => {
  const formMethods = useFormContext();

  const isDeclinable = useWatch({
    name: 'isDeclinable',
  });

  const availabilityType = useWatch({
    name: 'availabilityType',
  });

  const { append, remove, fields } = useFieldArray({
    name: 'productVariations',
  });

  const addProductVariation = () => {
    append(defaultProductVariationsFormValues);
  }

  const addIngredientsToAllProductVariations = () => {
    const { ingredients = [] } = formMethods.getValues();

    fields.forEach((_, index) => {
      formMethods.setValue(`productVariations.${index}.productVariationToIngredients`, ingredients);
    })
  }

  useEffect(() => {
    if (!isDeclinable) {
      const [_, ...fieldsIdx] = fields.map((_, index) => index);

      remove(fieldsIdx);
    }
  }, [isDeclinable]);

  return (
    <StyledForms.Form>

      <InputFile name="image" ref={ref} />

      <FormSection.Section>
        <FormSection.Title>Informations générales</FormSection.Title>
        <FormSection.Content>

          <TextInput
            name="name"
            label="Nom"
            autoCapitalize="none" />

          <TextArea
            name="description"
            label="Description"
            autoCapitalize="none" />

          <Toggle
            name='isAlcohol'
            options={[
              { title: "Sans alcool", value: false },
              { title: "Avec alcool", value: true },
            ]} />

          <TagSelect name='tags' isMultiple />

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Informations avancées</FormSection.Title>
        <FormSection.Content>

          <OptionSelect name='options' isMultiple />

          <AllergenSelect name="allergens" isMultiple />

          <KitchenAreaSelect
            name="kitchenAreas"
            isMultiple />

          {/* `zIndex` sur la rangée : la barre qui suit recouvrirait la bulle
              (même motif que PackForm). */}
          <Row justify="flex-end" style={{ zIndex: 10 }}>
            <InfoBulle texte={TEXTE_AIDE_HORAIRES} testID="aide-horaires" />
          </Row>

          <Toggle
            name="availabilityType"
            options={[
              { title: "Toujours disponible", value: ScheduleAvailabilityTypeEnum.always },
              { title: "Jours spécifiques", value: ScheduleAvailabilityTypeEnum.specificDays },
            ]} />

          {availabilityType === ScheduleAvailabilityTypeEnum.specificDays && (
            <ScheduleForm
              label="Horaires de disponibilité"
              aide={<InfoBulle texte={TEXTE_AIDE_HORAIRES} />}
              names={{
                schedule: 'availabilitySchedule',
                selectedDays: 'selectedDays',
                startTime: 'startTime',
                endTime: 'endTime',
              }} />
          )}

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Outil power-user</FormSection.Title>
        <FormSection.Content>

          <IngredientSelect
            name="ingredients"
            transform={{
              value: (item) => ({ ingredient: item, quantity: 1 }),
              accessor: (item) => (item.ingredient?._id),
              display: (item) => (item?.ingredient?.name)
            }}
            isMultiple />

          <Button
            title="Appliquer les ingrédients à toutes les déclinaisons"
            onPress={addIngredientsToAllProductVariations}
            large />

          <HelperText>
            Si toutes les déclinaisons utilisent les mêmes ingrédients, vous pouvez les appliquer à toutes les déclinaisons en une fois.
            <br />
            Ajouter les ingrédients ci-dessus, configurer vos déclinaisons et cliquer sur le bouton pour les appliquer à toutes les déclinaisons.
          </HelperText>

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Configuration</FormSection.Title>
        <FormSection.Content>
          <Toggle
            name='isDeclinable'
            options={[
              { title: "Produit unique", value: false },
              { title: "Produit déclinable", value: true },
            ]} />

          {fields.map((field, index) => (
            <ProductVariationForm
              key={field.id}
              index={index}
              isUnitary={!isDeclinable}
              remove={() => remove(index)}
              field={field} />
          ))}

          {isDeclinable && (
            <Button
              onPress={addProductVariation}
              title="Ajouter une déclinaison"
              bordered
              grow
              large
              square
              smallText />
          )}
        </FormSection.Content>
      </FormSection.Section>

    </StyledForms.Form>
  );
});
